// -----------------------------------------------------------
// FloraIQ - PointCloudViewerMock — Aba Visualizador 3D
// -----------------------------------------------------------

import { useState } from "react";
import { viewerLayers, selectedTreeExample } from "@/mocks/pointcloud";

export default function PointCloudViewerMock() {
  const [layers, setLayers] = useState(viewerLayers);
  const [selectedTool, setSelectedTool] = useState<string>("rotate");
  const [showObjectPanel, setShowObjectPanel] = useState(false);

  const toggleLayer = (id: string) => {
    setLayers((prev) => prev.map((l) => (l.id === id ? { ...l, visivel: !l.visivel } : l)));
  };

  const tools = [
    { id: "rotate", icon: "ri-rotate-lock-line", label: "Rotacionar" },
    { id: "measure-height", icon: "ri-ruler-line", label: "Medir altura" },
    { id: "measure-dist", icon: "ri-ruler-2-line", label: "Medir distância" },
    { id: "measure-crown", icon: "ri-bubble-chart-line", label: "Medir copa" },
    { id: "section", icon: "ri-scissors-cut-line", label: "Cortar seção" },
    { id: "select-tree", icon: "ri-pin-distance-line", label: "Selecionar árvore" },
    { id: "toggle-class", icon: "ri-palette-line", label: "Alternar classificação" },
    { id: "toggle-ortho", icon: "ri-image-line", label: "Ortofoto" },
    { id: "toggle-crown", icon: "ri-bubble-chart-line", label: "Copa estimada" },
    { id: "export-clip", icon: "ri-download-2-line", label: "Exportar recorte" },
  ];

  return (
    <div className="flex flex-col lg:flex-row gap-4">
      {/* Área central — simulador 3D */}
      <div className="flex-1 min-h-[480px] lg:min-h-[600px] bg-[#0D1117] rounded-card border border-background-200/70 relative overflow-hidden">
        {/* Grid de fundo */}
        <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.03) 1px, transparent 1px)", backgroundSize: "24px 24px" }}></div>

        {/* Pontos simulados no canvas */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Solo — pontos cinza */}
          {Array.from({ length: 50 }).map((_, i) => (
            <div
              key={`solo-${i}`}
              className="absolute w-1 h-1 rounded-full opacity-60"
              style={{
                left: `${5 + Math.random() * 80}%`,
                top: `${30 + Math.random() * 60}%`,
                backgroundColor: "#9E9E9E",
                animation: `fade-in ${1 + Math.random() * 2}s ease-out`,
              }}
            ></div>
          ))}
          {/* Vegetação — pontos verdes */}
          {Array.from({ length: 80 }).map((_, i) => (
            <div
              key={`veg-${i}`}
              className="absolute w-1.5 h-1.5 rounded-full opacity-70"
              style={{
                left: `${10 + Math.random() * 70}%`,
                top: `${5 + Math.random() * 75}%`,
                backgroundColor: i < 40 ? "#4CAF50" : i < 65 ? "#2E7D32" : "#1B5E20",
                boxShadow: "0 0 4px rgba(76, 175, 80, 0.5)",
                animation: `fade-in ${0.5 + Math.random() * 1.5}s ease-out`,
              }}
            ></div>
          ))}
          {/* Edificações — azuis */}
          {Array.from({ length: 20 }).map((_, i) => (
            <div
              key={`edif-${i}`}
              className="absolute w-1 h-1 rounded-full opacity-50"
              style={{
                left: `${15 + Math.random() * 50}%`,
                top: `${10 + Math.random() * 50}%`,
                backgroundColor: "#0052CC",
                animation: `fade-in ${1 + Math.random() * 2}s ease-out`,
              }}
            ></div>
          ))}
          {/* Interferências — laranjas/amarelos */}
          {Array.from({ length: 15 }).map((_, i) => (
            <div
              key={`int-${i}`}
              className="absolute w-2 h-2 rounded-full opacity-80"
              style={{
                left: `${20 + Math.random() * 50}%`,
                top: `${15 + Math.random() * 40}%`,
                backgroundColor: i < 7 ? "#F57C00" : "#FBC02D",
                boxShadow: "0 0 6px rgba(245, 124, 0, 0.6)",
                animation: `pulse ${2 + Math.random() * 2}s infinite`,
              }}
            ></div>
          ))}
          {/* Árvore selecionada destacada */}
          <div className="absolute left-[45%] top-[35%]">
            <div className="w-4 h-4 rounded-full bg-yellow-400 opacity-90 absolute -translate-x-1/2 -translate-y-1/2" style={{ boxShadow: "0 0 16px rgba(251, 192, 45, 0.8), 0 0 40px rgba(251, 192, 45, 0.4)" }}></div>
            <div className="w-12 h-12 rounded-full border-2 border-yellow-400/60 absolute -translate-x-1/2 -translate-y-1/2 animate-pulse"></div>
          </div>
        </div>

        {/* Overlay informativo */}
        <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm rounded-md px-3 py-1.5 text-xs text-white/80 font-mono">
          Nuvem: Central Tile 01 | 62,1 M pts | LAS 1.4 | EPSG:31982
        </div>

        {/* Coordenadas no canto */}
        <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-sm rounded-md px-3 py-1.5 text-xs text-white/60 font-mono">
          -16.679201, -49.255104 | Alt: 842,3 m
        </div>

        {/* Barra de ferramentas 3D */}
        <div className="absolute top-3 right-3 flex flex-col gap-0.5">
          {tools.slice(0, 7).map((tool) => (
            <button
              key={tool.id}
              onClick={() => { setSelectedTool(tool.id); if (tool.id === "select-tree") setShowObjectPanel(true); }}
              className={`w-8 h-8 flex items-center justify-center rounded-md bg-black/60 backdrop-blur-sm transition-colors cursor-pointer ${selectedTool === tool.id ? "text-yellow-400 border border-yellow-400/40" : "text-white/70 hover:text-white hover:bg-black/80"}`}
              title={tool.label}
              aria-label={tool.label}
            >
              <i className={`${tool.icon} text-sm`}></i>
            </button>
          ))}
        </div>

        {/* Aviso */}
        <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-sm rounded-md px-3 py-1.5">
          <p className="text-xs text-white/50 flex items-center gap-1.5">
            <i className="ri-information-line"></i>
            Visualização simulada — preparada para Potree/Cesium/Three.js
          </p>
        </div>
      </div>

      {/* Painéis laterais */}
      <div className="lg:w-[280px] shrink-0 space-y-4">
        {/* Painel de camadas */}
        <div className="bg-white rounded-card border border-background-200/70 p-3">
          <h4 className="text-xs font-bold text-foreground-900 font-heading mb-2 uppercase tracking-wide">Camadas</h4>
          <div className="space-y-0.5">
            {layers.map((layer) => (
              <button
                key={layer.id}
                onClick={() => toggleLayer(layer.id)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-background-50 transition-colors cursor-pointer text-left"
              >
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: layer.cor, opacity: layer.visivel ? 1 : 0.3 }}></span>
                <i className={`${layer.icone} text-xs ${layer.visivel ? "text-foreground-500" : "text-foreground-300"}`}></i>
                <span className={`text-xs flex-1 whitespace-nowrap ${layer.visivel ? "text-foreground-700" : "text-foreground-400"}`}>{layer.nome}</span>
                {layer.visivel ? (
                  <i className="ri-eye-line text-foreground-400 text-xs"></i>
                ) : (
                  <i className="ri-eye-off-line text-foreground-300 text-xs"></i>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Painel do objeto selecionado */}
        {showObjectPanel && (
          <div className="bg-white rounded-card border border-background-200/70 p-3 slide-in-from-right">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-bold text-foreground-900 font-heading uppercase tracking-wide">Árvore Selecionada</h4>
              <button onClick={() => setShowObjectPanel(false)} className="w-5 h-5 flex items-center justify-center rounded hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line text-foreground-400 text-xs"></i>
              </button>
            </div>
            <div className="space-y-2">
              <div>
                <span className="text-[10px] text-foreground-400 uppercase tracking-wide">Código CTA</span>
                <p className="text-sm font-bold text-foreground-900 font-heading">{selectedTreeExample.codigoCta}</p>
              </div>
              <div>
                <span className="text-[10px] text-foreground-400 uppercase tracking-wide">Espécie</span>
                <p className="text-xs text-foreground-700">{selectedTreeExample.especie}</p>
              </div>
              <div className="grid grid-cols-2 gap-2 pt-1 border-t border-background-100/50">
                {[
                  { l: "Altura", v: selectedTreeExample.alturaExtraida },
                  { l: "Diâm. Copa", v: selectedTreeExample.diametroCopa },
                  { l: "Vol. Copa", v: selectedTreeExample.volumeCopa },
                  { l: "Inclinação", v: selectedTreeExample.inclinacao },
                ].map((m) => (
                  <div key={m.l}>
                    <span className="text-[10px] text-foreground-400">{m.l}</span>
                    <p className="text-xs font-bold text-foreground-900 font-heading">{m.v}</p>
                  </div>
                ))}
              </div>
              <div className="pt-2 border-t border-background-100/50">
                <span className="text-[10px] text-foreground-400 uppercase tracking-wide">Interferências</span>
                <p className="text-xs text-orange-600">{selectedTreeExample.interferenciasProximas[0]}</p>
              </div>
              <div className="flex gap-1 pt-2">
                <button className="flex-1 text-[10px] py-1.5 rounded-md bg-primary-500 text-white hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">Ver FCA</button>
                <button className="flex-1 text-[10px] py-1.5 rounded-md bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">Histórico</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}